"use client"

import type React from "react"
import { useEffect, useState, useRef, useActionState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import Link from "next/link"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { submitContactForm, type ContactFormState } from "@/app/actions/contact"
import { useLanguage } from "@/lib/i18n"

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

export function ContactSection() {
  const { t, locale } = useLanguage()
  const sectionRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [magneticOffset, setMagneticOffset] = useState({ x: 0, y: 0 })
  const buttonRef = useRef<HTMLDivElement>(null)
  const [showContactModal, setShowContactModal] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  // Use server action with form state
  const [state, formAction, isPending] = useActionState<ContactFormState | null, FormData>(
    submitContactForm,
    null
  )

  // GSAP ScrollTrigger animation
  useEffect(() => {
    if (!sectionRef.current || !contentRef.current) return

    const ctx = gsap.context(() => {
      const contentElements = contentRef.current?.querySelectorAll(".contact-item")
      if (contentElements) {
        gsap.fromTo(
          contentElements,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.12,
            ease: "power3.out",
            scrollTrigger: {
              trigger: contentRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        )
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!buttonRef.current) return

    const rect = buttonRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    const deltaX = (e.clientX - centerX) * 0.15
    const deltaY = (e.clientY - centerY) * 0.15

    setMagneticOffset({ x: deltaX, y: deltaY })
  }

  const handleMouseLeave = () => {
    setMagneticOffset({ x: 0, y: 0 })
  }

  const handleLetsTalkClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowContactModal(true)
  }

  const handleEmailIconClick = (e: React.MouseEvent) => {
    e.preventDefault()
    window.location.href = "mailto:hi@raychard.co"
  }

  const closeModal = () => {
    setShowContactModal(false)
    formRef.current?.reset()
  }

  // Close modal and reset on successful submission
  useEffect(() => {
    if (state?.success) {
      const timer = setTimeout(() => {
        closeModal()
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [state?.success])

  return (
    <section
      ref={sectionRef}
      id="contact"
      data-section="contact"
      className="relative py-16 md:py-20 px-6 md:px-8 lg:px-12 overflow-hidden bg-[var(--color-bg-secondary)]"
    >
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          mixBlendMode: "overlay",
        }}
      />

      <div ref={contentRef} className="relative z-10 max-w-[600px] mx-auto text-center">
        <p
          className={`contact-item text-2xl md:text-3xl mb-6 text-[var(--color-accent-gold)] opacity-0${locale !== "zh" ? " rotate-[-2deg]" : ""}`}
          style={{ fontFamily: "var(--font-handwritten)" }}
        >
          {t("contact.sectionLabel")}
        </p>

        <p
          className="contact-item text-2xl md:text-3xl lg:text-4xl mt-4 leading-snug mb-12 max-w-[560px] mx-auto opacity-0 font-medium text-[var(--color-text-primary)]"
          style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}
        >
          {t("contact.headline1")}
          <br />
          {t("contact.headline2")}
        </p>

        <div
          ref={buttonRef}
          className="contact-item mt-8 opacity-0"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            transform: `translate(${magneticOffset.x}px, ${magneticOffset.y}px)`,
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <Button
            onClick={handleLetsTalkClick}
            size="lg"
            className="px-10 py-6 h-auto rounded-full font-medium hover:scale-[1.02] active:scale-[0.98] bg-[var(--color-accent-gold)] text-[var(--color-bg-primary)] hover:bg-[var(--color-accent-gold-bright)]"
            style={{
              transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {t("contact.letsTalk")}
          </Button>
        </div>

        <div className="contact-item flex items-center justify-center gap-5 mt-12 opacity-0">
          <motion.div whileHover={{ scale: 1.15, y: -2 }} whileTap={{ scale: 0.95 }}>
            <Link
              href="https://www.linkedin.com/in/hsinhsiangh/"
              target="_blank"
              rel="noopener noreferrer"
              className="relative flex items-center justify-center w-10 h-10 rounded-full text-[var(--color-text-secondary)] hover:text-[var(--color-accent-gold)] transition-all duration-300 focus-ring group"
              aria-label="LinkedIn"
            >
              <svg className="w-5 h-5 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              {/* Glow effect */}
              <span
                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background:
                    "radial-gradient(circle, var(--color-accent-gold) 0%, transparent 70%)",
                  filter: "blur(6px)",
                }}
              />
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.15, y: -2 }} whileTap={{ scale: 0.95 }}>
            <button
              onClick={handleEmailIconClick}
              className="relative flex items-center justify-center w-10 h-10 rounded-full text-[var(--color-text-secondary)] hover:text-[var(--color-accent-gold)] transition-all duration-300 focus-ring group"
              aria-label="Email"
            >
              <svg
                className="w-5 h-5 relative z-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              {/* Glow effect */}
              <span
                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background:
                    "radial-gradient(circle, var(--color-accent-gold) 0%, transparent 70%)",
                  filter: "blur(6px)",
                }}
              />
            </button>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {showContactModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            <motion.div
              className="relative bg-[var(--color-bg-secondary)] border border-[var(--color-bg-tertiary)] rounded-2xl shadow-2xl max-w-md w-full p-8"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-[var(--color-text-secondary)] hover:text-[var(--color-accent-gold)] transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div>
                <div className="mb-6 text-center">
                  <svg
                    className="w-12 h-12 mx-auto text-[var(--color-accent-gold)] mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <h3 className="text-2xl font-semibold text-[var(--color-text-primary)] mb-2">
                    {t("contact.getInTouch")}
                  </h3>
                  <p className="text-[var(--color-text-secondary)]">{t("contact.formSubtitle")}</p>
                </div>

                <form ref={formRef} action={formAction} className="space-y-4">
                  {/* Status Message */}
                  {state && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-lg text-sm ${
                        state.success
                          ? "bg-green-500/10 text-green-400 border border-green-500/20"
                          : "bg-red-500/10 text-red-400 border border-red-500/20"
                      }`}
                    >
                      {state.message}
                    </motion.div>
                  )}

                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2"
                    >
                      {t("contact.nameLabel")}
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      minLength={2}
                      className="w-full px-4 py-2 bg-[var(--color-bg-primary)] border border-[var(--color-bg-tertiary)] rounded-lg text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:ring-2 focus:ring-[var(--color-accent-gold)]/50 focus:border-[var(--color-accent-gold)]"
                      placeholder={t("contact.namePlaceholder")}
                    />
                    {state?.errors?.name && (
                      <p className="mt-1 text-xs text-red-400">{state.errors.name[0]}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2"
                    >
                      {t("contact.emailLabel")}
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2 bg-[var(--color-bg-primary)] border border-[var(--color-bg-tertiary)] rounded-lg text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:ring-2 focus:ring-[var(--color-accent-gold)]/50 focus:border-[var(--color-accent-gold)]"
                      placeholder={t("contact.emailPlaceholder")}
                    />
                    {state?.errors?.email && (
                      <p className="mt-1 text-xs text-red-400">{state.errors.email[0]}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2"
                    >
                      {t("contact.messageLabel")}
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      minLength={10}
                      className="w-full px-4 py-2 bg-[var(--color-bg-primary)] border border-[var(--color-bg-tertiary)] rounded-lg text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:ring-2 focus:ring-[var(--color-accent-gold)]/50 focus:border-[var(--color-accent-gold)] min-h-[120px]"
                      placeholder={t("contact.messagePlaceholder")}
                    />
                    {state?.errors?.message && (
                      <p className="mt-1 text-xs text-red-400">{state.errors.message[0]}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isPending}
                    className="w-full py-6 rounded-full font-medium bg-[var(--color-accent-gold)] text-[var(--color-bg-primary)] hover:bg-[var(--color-accent-gold-bright)] hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                    style={{
                      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                  >
                    {isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        {t("contact.sending")}
                      </span>
                    ) : (
                      t("contact.sendMessage")
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
